<script lang="ts" setup>
import { useScrollLock } from '@vueuse/core';
import { UiBtn, UiIcon } from '@shared/ui';
import { menuContent } from '../Menu/content';
import type { MobileMenuEmits } from '../types';
import type { MenuKey, AccordionState, Section, MobileMenuProps } from './types';
import { useRoute } from '#imports';

const route = useRoute();

const props = defineProps<MobileMenuProps>();
const emit = defineEmits<MobileMenuEmits>();
const isMounted = ref(false);

const accordionState = ref<AccordionState>({
  KPlus: false,
  SeminarsAndCourses: false,
  About: false,
  Services: false,
});

const sections: Section[] = [
  { key: 'KPlus', title: 'КонсультантПлюс' },
  { key: 'SeminarsAndCourses', title: 'Семинары и курсы' },
  { key: 'About', title: 'О компании' },
  { key: 'Services', title: 'Другие услуги' },
];

const toggleAccordion = (key: MenuKey) => {
  accordionState.value[key] = !accordionState.value[key];
};

const isLocked = useScrollLock(document?.body);

const closeMenu = () => emit('close');

watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    isLocked.value = true;
  }
  else {
    isLocked.value = false;
  }
});

watch(
  () => route.path,
  (newPath, oldPath) => {
    if (newPath !== oldPath) closeMenu();
  },
);

onMounted(async () => {
  await nextTick();
  isMounted.value = true;
});

onUnmounted(() => {
  isLocked.value = false;
});
</script>

<template>
  <Transition name="fade">
    <article
      v-if="isMounted && isOpen"
      class="fixed inset-0 bg-white z-50 top-29.75 max-[744px]:top-15.25 max-[893px]:px-7.5 max-[450px]:px-3 px-3 overflow-y-auto"
      @click.self="emit('close')"
    >
      <div class="flex flex-col gap-2 mt-2">
        <UiBtn
          tag="a"
          href="/consultant-plus/probnyj-dostup-besplatno"
          label="Пробный доступ"
          class="hidden max-[744px]:!inline text-sm !py-3 !px-6 !bg-blue-600 !text-white rounded-full transition-colors w-full text-center"
        />
        <UiBtn
          href="/consultant-plus/demonstraciya-konsultantplyus"
          tag="a"
          class="hidden max-[744px]:!inline text-sm text-blue-600 transition-colors w-full text-center !border-none"
          label="Заказать демонстрацию"
        />
      </div>

      <div class="py-5 flex flex-col gap-6 max-[744px]:pt-6">
        <div class="flex flex-col gap-6">
          <button
            class="flex items-center w-full text-xl font-semibold"
            @click="toggleAccordion('KPlus')"
          >
            <span class="text-lg">{{ sections[0]?.title }}</span>
            <UiIcon
              :name="accordionState.KPlus ? 'chevron-up' : 'chevron-down'"
              class="w-5 h-5 ml-2 text-gray-500"
            />
          </button>

          <div
            v-if="accordionState.KPlus"
            class="grid grid-cols-2 max-[500px]:grid-cols-1 gap-8"
          >
            <div class="flex flex-col gap-8">
              <template
                v-for="(blockGroup, groupIndex) in menuContent.KPlus?.left"
                :key="`left-${groupIndex}`"
              >
                <div
                  v-for="(block, blockIndex) in blockGroup.blocks"
                  :key="`left-block-${blockIndex}`"
                  class="flex flex-col gap-3"
                >
                  <h3 class="font-bold !text-sm text-gray-900">
                    {{ block.title }}
                  </h3>
                  <ul class="flex flex-col gap-3">
                    <li
                      v-for="(item, itemIndex) in block.blocks"
                      :key="`left-item-${itemIndex}`"
                    >
                      <NuxtLink
                        :to="item.link"
                        class="flex py-1 rounded-lg transition text-sm"
                      >
                        {{ item.label }}
                      </NuxtLink>
                    </li>
                  </ul>
                  <NuxtLink
                    v-if="block.footerButton"
                    :to="block.footerButton.link"
                    class="text-blue-600 text-sm underline hover:no-underline mt-1"
                  >
                    {{ block.footerButton.label }}
                  </NuxtLink>
                </div>
              </template>
            </div>

            <div class="flex flex-col gap-8">
              <template
                v-for="(blockGroup, groupIndex) in menuContent.KPlus?.right"
                :key="`right-${groupIndex}`"
              >
                <div
                  v-for="(block, blockIndex) in blockGroup.blocks"
                  :key="`right-block-${blockIndex}`"
                  class="flex flex-col gap-3"
                >
                  <h3 class="font-bold !text-sm text-gray-900">
                    {{ block.title }}
                  </h3>
                  <ul class="flex flex-col gap-3">
                    <li
                      v-for="(item, itemIndex) in block.blocks"
                      :key="`right-item-${itemIndex}`"
                    >
                      <NuxtLink
                        :to="item.link"
                        class="flex py-1 rounded-lg transition text-sm"
                      >
                        {{ item.label }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
              </template>
            </div>
          </div>
        </div>

        <NuxtLink
          to="/expertum"
          class="text-lg font-semibold rounded-lg transition"
          external
        >
          Экспертум
        </NuxtLink>

        <template
          v-for="section in sections.slice(1)"
          :key="section.key"
        >
          <div class="flex flex-col gap-6">
            <button
              class="flex items-center w-full text-xl font-semibold"
              @click="toggleAccordion(section.key)"
            >
              <span class="text-lg">{{ section.title }}</span>
              <UiIcon
                :name="accordionState[section.key] ? 'chevron-up' : 'chevron-down'"
                class="w-5 h-5 ml-2 text-gray-500"
              />
            </button>

            <div
              v-if="accordionState[section.key]"
              class="grid grid-cols-2 max-[500px]:grid-cols-1 gap-8"
            >
              <template v-if="section.key === 'Services'">
                <div class="flex flex-col gap-4 col-span-2">
                  <NuxtLink
                    v-for="(block, index) in menuContent[section.key]?.left[0]?.blocks"
                    :key="`service-${index}`"
                    :to="block.link"
                    class="flex flex-col gap-2 rounded-xl transition"
                  >
                    <p class="font-bold text-sm">{{ block.title }}</p>
                    <p class="text-sm text-gray-600">
                      {{ block.label }}
                    </p>
                  </NuxtLink>
                </div>
              </template>

              <template v-else>
                <div class="flex flex-col gap-8">
                  <template
                    v-for="(blockGroup, groupIndex) in menuContent[section.key]?.left"
                    :key="`left-${groupIndex}`"
                  >
                    <div
                      v-for="(block, blockIndex) in blockGroup.blocks"
                      :key="`left-block-${blockIndex}`"
                      class="flex flex-col gap-3"
                    >
                      <h3 class="font-bold !text-sm text-gray-900">
                        {{ block.title }}
                      </h3>
                      <ul class="flex flex-col gap-3">
                        <li
                          v-for="(item, itemIndex) in block.blocks"
                          :key="`left-item-${itemIndex}`"
                        >
                          <NuxtLink
                            :to="item.link"
                            class="flex py-1 rounded-lg transition text-sm"
                          >
                            {{ item.label }}
                          </NuxtLink>
                        </li>
                      </ul>
                      <NuxtLink
                        v-if="block.footerButton"
                        :to="block.footerButton.link"
                        class="text-blue-600 text-sm underline hover:no-underline mt-1"
                      >
                        {{ block.footerButton.label }}
                      </NuxtLink>
                    </div>
                  </template>
                </div>

                <div class="flex flex-col gap-8">
                  <template
                    v-for="(blockGroup, groupIndex) in menuContent[section.key]?.right"
                    :key="`right-${groupIndex}`"
                  >
                    <div
                      v-for="(block, blockIndex) in blockGroup.blocks"
                      :key="`right-block-${blockIndex}`"
                      class="flex flex-col gap-3"
                    >
                      <h3 class="font-bold !text-sm text-gray-900">
                        {{ block.title }}
                      </h3>
                      <ul class="flex flex-col gap-3">
                        <li
                          v-for="(item, itemIndex) in block.blocks"
                          :key="`right-item-${itemIndex}`"
                        >
                          <NuxtLink
                            :to="item.link"
                            class="flex py-1 rounded-lg transition text-sm"
                          >
                            {{ item.label }}
                          </NuxtLink>
                        </li>
                      </ul>
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </article>
  </Transition>
</template>
